// --- Core Dependencies
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
// --- UI Components
import { Col, Row } from 'antd';
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import Select from '@dtci/spellbook/Select';
import { MultiSelect } from '@dtci/spellbook/MultiSelect';
import { InputLabel } from '../../../../components/InputLabel/InputLabel';
// --- Hooks
import { useSharedSectionsActions } from '../../../../store/sharedSections/useSharedSections';
import { useLoading } from '../../../../utils/hooks/useLoading';
// --- API and Utilities
import { datastageApi } from '../../../../api';
import { filterCriteria } from '../../../../utils/filterCriteria';
// --- Styles
import './styles.scss';

/**
 * SharedSectionsFilterForm Component
 * Renders a filter form for shared sections with inputs, dropdowns, and buttons.
 *
 * @returns {JSX.Element} The Shared Sections Filter Form component.
 */
export function SharedSectionsFilterForm() {
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { updateSharedSections, updateSharedSectionsForm, clearSharedSections } =
        useSharedSectionsActions();
    const { filterForm, defaultFilterRange, sectionSort } = useSelector((state) => state.sharedSections);
    const { usersList } = useSelector((state) => state.users);

    // Fetch shared sections data when the component mounts
    useEffect(() => {
        setTimeout(() => fetchSharedSections(filterForm), 5);
    }, []);

    // Fetch shared sections based on filters
    const fetchSharedSections = async (filters = {}) => {
        try {
            setLoadingTrue();
            const data = await datastageApi.sharedSections(
                filterCriteria(filters, '', 'sharedSections'),
                defaultFilterRange,
                sectionSort,
            );
            updateSharedSections({
                shared: data?.content,
                totalPages: data?.totalPages,
                totalItems: data?.totalElements,
                currentPage: data?.pageable?.pageNumber,
            });
        } catch (error) {
            console.error('Error fetching shared sections:', error);
        } finally {
            setLoadingFalse();
        }
    };

    // Handle the filter button click
    const handleFilter = () => {
        fetchSharedSections(filterForm);
    };

    // Handle the reset button click
    const handleResetFilter = async () => {
        try {
            setLoadingTrue();
            clearSharedSections();
            await fetchSharedSections({});
        } catch (error) {
            console.error('Error resetting filter:', error);
        } finally {
            setLoadingFalse();
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter') {
            handleFilter();
        }
    };

    return (
        <Row className="shared-sections-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Section Name">
                    <Input
                        value={filterForm.title}
                        onChange={(e) =>
                            updateSharedSectionsForm({
                                key: 'filterForm',
                                data: { title: e.target.value },
                            })
                        }
                        onKeyDown={handleKeyDown}
                        width="auto"
                        id="title"
                        name="title"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Section Text">
                    <Input
                        value={filterForm.bodyText}
                        onChange={(e) =>
                            updateSharedSectionsForm({
                                key: 'filterForm',
                                data: { bodyText: e.target.value },
                            })
                        }
                        onKeyDown={handleKeyDown}
                        width="auto"
                        id="bodyText"
                        name="bodyText"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Users">
                    {/* Render dropdown for selecting users */}
                    <MultiSelect
                        onChange={(value) =>
                            updateSharedSectionsForm({ key: 'filterForm', data: { id: value } })
                        }
                        name="class"
                        isSearchable
                        options={usersList}
                        isClearable
                        value={filterForm.id}
                    />
                </InputLabel>
            </Col>

            <Col>
                <InputLabel colProps={{ span: 24 }} label="Private/Public">
                    {/* Render dropdown for selecting public/private status */}
                    <Select
                        onChange={(value) =>
                            updateSharedSectionsForm({
                                key: 'filterForm',
                                data: { publicPrivate: value },
                            })
                        }
                        name="Public/Private"
                        options={[
                            { label: 'PUBLIC', value: 'PUBLIC' },
                            { label: 'PRIVATE', value: 'PRIVATE' },
                        ]}
                        isClearable
                        isSearchable
                        customClear={false}
                        value={filterForm.publicPrivate}
                    />
                </InputLabel>
            </Col>

            <Col>
                <Button
                    className="filter-button"
                    sizeType="small"
                    styleType="promoted"
                    onClick={handleFilter}
                >
                    Filter
                </Button>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
